import { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader2, X, Play, Plus, Edit2, Trash2, ChevronDown, ChevronRight, CheckCircle, XCircle, Download, FileText, StopCircle } from 'lucide-react';
import { HallucinationApiService } from '../services/hallucinationApi';
import {
  HallucinationTest,
  HallucinationRun,
  HallucinationTestResult,
  CreateHallucinationTestRequest,
  UpdateHallucinationTestRequest,
  StartRunRequest,
} from '../types/hallucination';
import { AppLayout } from './AppLayout';
import { HallucinationRunModal, HallucinationRunSelection } from './HallucinationRunModal';
import { ExecutionHistoryDetail } from './ExecutionHistoryDetail';

/** Mini sparkline showing last N scores for a test */
const TrendSparkline: React.FC<{ scores: number[] }> = ({ scores }) => {
  if (scores.length === 0) return <span className="text-xs text-gray-400">—</span>;

  const max = 10;
  const height = 20;
  const width = Math.min(scores.length * 8, 64);
  const step = width / Math.max(scores.length - 1, 1);

  const points = scores.map((s, i) => {
    const x = i * step;
    const y = height - (s / max) * height;
    return `${x},${y}`;
  }).join(' ');

  const lastScore = scores[scores.length - 1];
  const color = lastScore >= 8 ? '#16a34a' : lastScore >= 5 ? '#ca8a04' : '#dc2626';

  return (
    <div className="flex items-center gap-2">
      <svg width={width} height={height} className="overflow-visible">
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Dot on last point */}
        {scores.length > 0 && (
          <circle
            cx={scores.length === 1 ? 0 : (scores.length - 1) * step}
            cy={height - (lastScore / max) * height}
            r="2.5"
            fill={color}
          />
        )}
      </svg>
      <span className={`text-xs font-medium ${lastScore >= 8 ? 'text-green-600' : lastScore >= 5 ? 'text-yellow-600' : 'text-red-600'}`}>
        {lastScore}/10
      </span>
    </div>
  );
};

export const HallucinationTestsPage: React.FC = () => {
  const api = HallucinationApiService.getInstance();

  // Tab state
  const [activeTab, setActiveTab] = useState<'tests' | 'runs'>('tests');

  // Tests state
  const [tests, setTests] = useState<HallucinationTest[]>([]);
  const [testsLoading, setTestsLoading] = useState(true);
  const [testsError, setTestsError] = useState<string | null>(null);

  // Runs state
  const [runs, setRuns] = useState<HallucinationRun[]>([]);
  const [runsLoading, setRunsLoading] = useState(true);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
  const [runResults, setRunResults] = useState<Record<string, HallucinationTestResult[]>>({});

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingTest, setEditingTest] = useState<HallucinationTest | null>(null);
  const [formName, setFormName] = useState('');
  const [formPrompt, setFormPrompt] = useState('');
  const [formExpectation, setFormExpectation] = useState('');
  const [formIsActive, setFormIsActive] = useState(true);
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Run modal state
  const [showRunModal, setShowRunModal] = useState(false);
  const [runningRunId, setRunningRunId] = useState<string | null>(null);

  // Import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importText, setImportText] = useState('');
  const [importError, setImportError] = useState<string | null>(null);
  const [importLoading, setImportLoading] = useState(false);

  // Execution detail state
  const [selectedExecutionId, setSelectedExecutionId] = useState<string | null>(null);
  const [selectedResult, setSelectedResult] = useState<HallucinationTestResult | null>(null);

  // Trend data: all results from completed runs, keyed by testId → scores (chronological)
  const [trendResults, setTrendResults] = useState<HallucinationTestResult[]>([]);
  const [trendLoading, setTrendLoading] = useState(false);

  // Load tests
  const loadTests = useCallback(async () => {
    try {
      setTestsError(null);
      const data = await api.listTests(true);
      // Sort: active first, then by name
      const sorted = [...data].sort((a, b) => {
        if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
        return a.name.localeCompare(b.name);
      });
      setTests(sorted);
    } catch (err: any) {
      setTestsError(err.message || 'Failed to load tests');
    } finally {
      setTestsLoading(false);
    }
  }, [api]);

  // Load runs
  const loadRuns = useCallback(async () => {
    try {
      setRunsError(null);
      const data = await api.listRuns();
      // Sort by date desc
      const sorted = [...data].sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
      setRuns(sorted);
    } catch (err: any) {
      setRunsError(err.message || 'Failed to load runs');
    } finally {
      setRunsLoading(false);
    }
  }, [api]);

  // Load run results
  const loadRunResults = useCallback(async (runId: string, force = false) => {
    if (!force && runResults[runId]) return; // Already loaded
    try {
      const results = await api.getRunResults(runId);
      setRunResults(prev => ({ ...prev, [runId]: results }));
    } catch (err) {
      console.error('Failed to load run results:', err);
    }
  }, [api]);

  useEffect(() => {
    loadTests();
    loadRuns();
  }, [loadTests, loadRuns]);

  // Load trend data from completed runs
  useEffect(() => {
    const loadTrends = async () => {
      const completedRuns = runs.filter(r => r.status === 'Completed');
      if (completedRuns.length === 0) return;

      setTrendLoading(true);
      try {
        // Load results for up to last 8 completed runs
        const recent = completedRuns.slice(0, 8);
        const allResults: HallucinationTestResult[] = [];
        for (const run of recent) {
          if (runResults[run.id]) {
            allResults.push(...runResults[run.id]);
          } else {
            const results = await api.getRunResults(run.id);
            setRunResults(prev => ({ ...prev, [run.id]: results }));
            allResults.push(...results);
          }
        }
        setTrendResults(allResults);
      } catch (err) {
        console.error('Failed to load trend data:', err);
      } finally {
        setTrendLoading(false);
      }
    };

    if (!runsLoading && runs.length > 0) {
      loadTrends();
    }
  }, [runs, runsLoading]);

  // Compute per-test trend scores (chronological order, newest last)
  const testTrends = useMemo(() => {
    const map: Record<string, number[]> = {};
    // Group by testId, sort by scoredAt
    const sorted = [...trendResults].sort(
      (a, b) => new Date(a.scoredAt).getTime() - new Date(b.scoredAt).getTime()
    );
    for (const r of sorted) {
      if (!map[r.testId]) map[r.testId] = [];
      map[r.testId].push(r.score);
    }
    // Keep only last 8 scores per test
    for (const key of Object.keys(map)) {
      if (map[key].length > 8) map[key] = map[key].slice(-8);
    }
    return map;
  }, [trendResults]);

  // Polling for running run
  useEffect(() => {
    if (!runningRunId) return;

    const pollInterval = setInterval(async () => {
      const run = await api.getRun(runningRunId);
      if (!run) {
        setRunningRunId(null);
        loadRuns();
        return;
      }

      // Update run in list
      setRuns(prev => prev.map(r => r.id === run.id ? run : r));

      // Refresh results if this run is expanded
      if (expandedRunId === runningRunId) {
        loadRunResults(runningRunId, true);
      }

      if (run.status === 'Completed' || run.status === 'Failed' || run.status === 'Cancelled') {
        setRunningRunId(null);
        loadRuns();
        if (expandedRunId === runningRunId) {
          loadRunResults(runningRunId, true);
        }
      }
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [runningRunId, expandedRunId, api, loadRuns, loadRunResults]);

  // Handlers
  const handleNewTest = () => {
    setEditingTest(null);
    setFormName('');
    setFormPrompt('');
    setFormExpectation('');
    setFormIsActive(true);
    setFormError(null);
    setShowForm(true);
  };

  const handleEditTest = (test: HallucinationTest) => {
    setEditingTest(test);
    setFormName(test.name);
    setFormPrompt(test.prompt);
    setFormExpectation(test.expectation);
    setFormIsActive(test.isActive);
    setFormError(null);
    setShowForm(true);
  };

  const handleSaveTest = async () => {
    if (!formName.trim() || !formPrompt.trim() || !formExpectation.trim()) {
      setFormError('All fields are required');
      return;
    }

    setFormSaving(true);
    setFormError(null);

    try {
      if (editingTest) {
        const request: UpdateHallucinationTestRequest = {
          name: formName.trim(),
          prompt: formPrompt.trim(),
          expectation: formExpectation.trim(),
          isActive: formIsActive,
        };
        await api.updateTest(editingTest.id, request);
      } else {
        const request: CreateHallucinationTestRequest = {
          name: formName.trim(),
          prompt: formPrompt.trim(),
          expectation: formExpectation.trim(),
          isActive: formIsActive,
        };
        await api.createTest(request);
      }

      setShowForm(false);
      loadTests();
    } catch (err: any) {
      setFormError(err.message || 'Failed to save test');
    } finally {
      setFormSaving(false);
    }
  };

  const handleDeleteTest = async (testId: string) => {
    if (!confirm('Delete this test?')) return;

    try {
      await api.deleteTest(testId);
      loadTests();
    } catch (err: any) {
      alert(err.message || 'Failed to delete test');
    }
  };

  const handleToggleActive = async (test: HallucinationTest) => {
    try {
      await api.toggleTest(test.id, !test.isActive);
      loadTests();
    } catch (err: any) {
      alert(err.message || 'Failed to toggle test');
    }
  };

  const handleRunAllActive = () => {
    setShowRunModal(true);
  };

  const handleStartRun = async (selection: HallucinationRunSelection) => {
    // Get active tests count
    const activeTests = tests.filter(t => t.isActive);
    if (activeTests.length === 0) {
      alert('No active tests to run');
      return;
    }

    setShowRunModal(false);

    let runRequest: StartRunRequest;

    if (selection.customConfig) {
      const config = selection.customConfig;
      runRequest = {
        id: config.id,
        name: config.name,
        orchestrator: config.config.orchestrator,
        researcher: config.config.researcher,
        reducer: config.config.reducer,
        writer: config.config.writer,
        relevancechecker: config.config.relevancechecker,
      };
    } else {
      runRequest = {
        id: selection.providerId!,
        name: selection.providerId!,
        providerId: selection.providerId,
      };
    }

    try {
      const runId = await api.startRun(runRequest);
      setRunningRunId(runId);
    } catch (err: any) {
      alert(err.message || 'Failed to start run');
    }
  };

  const handleImportTests = async () => {
    if (!importText.trim()) {
      setImportError('Please paste JSON to import');
      return;
    }

    setImportError(null);
    setImportLoading(true);

    try {
      const data = JSON.parse(importText);

      // Accept both array and single object
      const items: Array<{ name: string; prompt: string; expectation: string; isActive?: boolean }> = Array.isArray(data) ? data : [data];

      if (items.length === 0) {
        setImportError('No tests found in JSON');
        setImportLoading(false);
        return;
      }

      // Validate shape
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (!item.name || !item.prompt || !item.expectation) {
          setImportError(`Item ${i + 1} missing required field (name, prompt, expectation)`);
          setImportLoading(false);
          return;
        }
      }

      // Deduplicate against existing tests (match by name + prompt)
      const existingKeys = new Set(
        tests.map(t => `${t.name.trim().toLowerCase()}||${t.prompt.trim().toLowerCase()}`)
      );

      const newItems = items.filter(item => {
        const key = `${item.name.trim().toLowerCase()}||${item.prompt.trim().toLowerCase()}`;
        return !existingKeys.has(key);
      });

      if (newItems.length === 0) {
        setImportError(`All ${items.length} test(s) already exist — nothing to import`);
        setImportLoading(false);
        return;
      }

      // Create only new tests
      let created = 0;
      for (const item of newItems) {
        await api.createTest({
          name: item.name,
          prompt: item.prompt,
          expectation: item.expectation,
          isActive: item.isActive !== false,
        });
        created++;
      }

      const skipped = items.length - created;

      setShowImportModal(false);
      setImportText('');
      loadTests();
      const msg = skipped > 0
        ? `Imported ${created} test(s), skipped ${skipped} duplicate(s)`
        : `Imported ${created} test(s) successfully`;
      alert(msg);
    } catch (err: any) {
      if (err instanceof SyntaxError) {
        setImportError('Invalid JSON format');
      } else {
        setImportError(err.message || 'Import failed');
      }
    } finally {
      setImportLoading(false);
    }
  };

  const handleCancelRun = async (runId: string) => {
    try {
      await api.cancelRun(runId);
      setRunningRunId(null);
      loadRuns();
    } catch (err: any) {
      alert(err.message || 'Failed to cancel run');
    }
  };

  const handleExpandRun = (runId: string) => {
    if (expandedRunId === runId) {
      setExpandedRunId(null);
    } else {
      setExpandedRunId(runId);
      loadRunResults(runId);
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleString();
  };

  const truncate = (str: string, len: number) => {
    return str.length > len ? str.substring(0, len) + '...' : str;
  };

  return (
    <AppLayout>
    <div data-component="HallucinationTestsPage" className="flex flex-col h-full">

      {/* Tabs */}
      <div className="border-b border-gray-200 bg-white px-6">
        <nav className="flex gap-6">
          <button
            onClick={() => setActiveTab('tests')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'tests'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Tests
          </button>
          <button
            onClick={() => setActiveTab('runs')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'runs'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Runs
          </button>
        </nav>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 bg-gray-50">
        {/* Tests Tab */}
        {activeTab === 'tests' && (
          <div>
            {/* Toolbar */}
            <div className="mb-4 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">Test Cases</h2>
              <div className="flex gap-2">
                <button
                  onClick={handleRunAllActive}
                  disabled={runningRunId !== null || tests.filter(t => t.isActive).length === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Play className="w-4 h-4" />
                  Run All Active
                </button>
                <button
                  onClick={() => {
                    const activeTests = tests.filter(t => t.isActive);
                    if (activeTests.length === 0) { alert('No active tests to export'); return; }
                    const exportData = activeTests.map(({ name, prompt, expectation }) => ({ name, prompt, expectation }));
                    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'hallucination-tests.json';
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  disabled={tests.filter(t => t.isActive).length === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Export
                </button>
                <button
                  onClick={() => { setImportText(''); setImportError(null); setShowImportModal(true); }}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Import
                </button>
                <button
                  onClick={handleNewTest}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  New Test
                </button>
              </div>
            </div>

            {testsLoading ? (
              <div className="flex items-center gap-2 text-gray-600">
                <Loader2 className="w-5 h-5 animate-spin" />
                Loading tests...
              </div>
            ) : testsError ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                {testsError}
              </div>
            ) : tests.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-500">
                No hallucination tests yet. Click "New Test" to create one.
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-12">Active</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prompt</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expectation</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-28">Trend</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase w-32">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {tests.map(test => (
                      <tr key={test.id} className={test.isActive ? 'bg-white' : 'bg-gray-50 opacity-60'}>
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={test.isActive}
                            onChange={() => handleToggleActive(test)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{test.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600" title={test.prompt}>
                          {truncate(test.prompt, 80)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600" title={test.expectation}>
                          {truncate(test.expectation, 80)}
                        </td>
                        <td className="px-4 py-3">
                          {trendLoading ? (
                            <Loader2 className="w-3 h-3 animate-spin text-gray-400" />
                          ) : (
                            <TrendSparkline scores={testTrends[test.id] || []} />
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => handleEditTest(test)}
                              className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteTest(test.id)}
                              className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {runningRunId && (
              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                  <span className="text-blue-700">Run in progress... (ID: {runningRunId.slice(0, 8)}...)</span>
                </div>
                <button
                  onClick={() => handleCancelRun(runningRunId)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
                >
                  <StopCircle className="w-4 h-4" />
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}

        {/* Runs Tab */}
        {activeTab === 'runs' && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Run History</h2>

            {runsLoading ? (
              <div className="flex items-center gap-2 text-gray-600">
                <Loader2 className="w-5 h-5 animate-spin" />
                Loading runs...
              </div>
            ) : runsError ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                {runsError}
              </div>
            ) : runs.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-500">
                No runs yet. Run some tests to see results here.
              </div>
            ) : (
              <div className="space-y-2">
                {runs.map(run => (
                  <div key={run.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    {/* Run Header */}
                    <div
                      className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                      onClick={() => handleExpandRun(run.id)}
                    >
                      <div className="flex items-center gap-3">
                        {expandedRunId === run.id ? (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {formatDate(run.startedAt)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {run.providerConfig?.name || 'Custom Config'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {/* Progress indicator */}
                        {run.status === 'Running' && (
                          <div className="flex items-center gap-2">
                            <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-blue-500 rounded-full transition-all duration-300"
                                style={{ width: `${run.totalTests > 0 ? (run.completedTests / run.totalTests) * 100 : 0}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-600 whitespace-nowrap">
                              {run.completedTests}/{run.totalTests}
                            </span>
                          </div>
                        )}

                        {/* Status Badge */}
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          run.status === 'Running' ? 'bg-blue-100 text-blue-700' :
                          run.status === 'Completed' ? 'bg-green-100 text-green-700' :
                          run.status === 'Failed' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {run.status === 'Running' && <Loader2 className="w-3 h-3 inline mr-1 animate-spin" />}
                          {run.status}
                        </span>

                        {/* Cancel button for running runs */}
                        {run.status === 'Running' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleCancelRun(run.id); }}
                            className="flex items-center gap-1 px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors"
                            title="Cancel run"
                          >
                            <StopCircle className="w-3 h-3" />
                            Cancel
                          </button>
                        )}

                        {/* Pass Rate */}
                        {run.status === 'Completed' && (
                          <span className="text-sm text-gray-600">
                            {run.passedTests}/{run.totalTests} passed
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Expanded Results */}
                    {expandedRunId === run.id && (
                      <div className="border-t border-gray-200 bg-gray-50 p-4">
                        {runResults[run.id] ? (
                          <table className="min-w-full bg-white rounded-lg overflow-hidden">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Test</th>
                                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Score</th>
                                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Pass</th>
                                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Exec Status</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Reasoning</th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Time</th>
                                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 w-10"></th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {runResults[run.id].map(result => (
                                <tr key={result.id} className="text-sm cursor-pointer hover:bg-gray-50" onClick={() => { setSelectedExecutionId(result.executionId); setSelectedResult(result); }}
>
                                  <td className="px-3 py-2 text-gray-900">{result.testName}</td>
                                  <td className="px-3 py-2 text-center">
                                    <span className={`font-medium ${
                                      result.score >= 8 ? 'text-green-600' :
                                      result.score >= 5 ? 'text-yellow-600' :
                                      'text-red-600'
                                    }`}>
                                      {result.score}/10
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    {result.passed ? (
                                      <CheckCircle className="w-5 h-5 text-green-600 inline" />
                                    ) : (
                                      <XCircle className="w-5 h-5 text-red-600 inline" />
                                    )}
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    <span className={`px-1.5 py-0.5 text-xs rounded ${
                                      result.responseStatus === 'success' ? 'bg-green-100 text-green-700' :
                                      result.responseStatus === 'direct_search' ? 'bg-cyan-100 text-cyan-700' :
                                      result.responseStatus === 'need_explanation' ? 'bg-orange-100 text-orange-700' :
                                      result.responseStatus === 'error' ? 'bg-red-100 text-red-700' :
                                      'bg-gray-100 text-gray-700'
                                    }`}>
                                      {result.responseStatus}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 text-gray-600 max-w-md">
                                    <div className="truncate" title={result.reasoning}>
                                      {result.reasoning}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 text-right text-gray-500">
                                    {(result.executionTimeMs / 1000).toFixed(1)}s
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    {(() => {
                                      const test = tests.find(t => t.id === result.testId);
                                      const isActive = test?.isActive ?? false;
                                      return (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (test) handleEditTest(test);
                                          }}
                                          className={`p-1 rounded ${isActive ? 'text-green-600 hover:text-green-700 hover:bg-green-50' : 'text-gray-400 hover:text-gray-500 hover:bg-gray-100'}`}
                                          title={isActive ? 'Test active' : 'Test inactive/deleted'}
                                        >
                                          <FileText className="w-4 h-4" />
                                        </button>
                                      );
                                    })()}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <div className="flex items-center gap-2 text-gray-600">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Loading results...
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Test Form Modal */}
      {showForm && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={() => setShowForm(false)} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900">
                  {editingTest ? 'Edit Test' : 'New Test'}
                </h2>
                <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                {formError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                    {formError}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={formName}
                    onChange={e => setFormName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Contract Formation Basics"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prompt *</label>
                  <textarea
                    value={formPrompt}
                    onChange={e => setFormPrompt(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="The prompt that will be sent to the AI..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expectation *</label>
                  <textarea
                    value={formExpectation}
                    onChange={e => setFormExpectation(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="What the AI response should contain or validate against..."
                  />
                </div>

                {editingTest && (
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="formIsActive"
                      checked={formIsActive}
                      onChange={e => setFormIsActive(e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="formIsActive" className="text-sm text-gray-700">Active</label>
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveTest}
                  disabled={formSaving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {formSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editingTest ? 'Save Changes' : 'Create Test'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Run Modal */}
      {showRunModal && (
        <HallucinationRunModal
          activeTestCount={tests.filter(t => t.isActive).length}
          onClose={() => setShowRunModal(false)}
          onStart={handleStartRun}
        />
      )}

      {/* Import Modal */}
      {showImportModal && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={() => setShowImportModal(false)} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900">Import Tests</h2>
                <button onClick={() => setShowImportModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                <p className="text-sm text-gray-600">
                  Paste JSON array of tests. Each item needs: <code className="bg-gray-100 px-1 rounded">name</code>, <code className="bg-gray-100 px-1 rounded">prompt</code>, <code className="bg-gray-100 px-1 rounded">expectation</code>. Optional: <code className="bg-gray-100 px-1 rounded">isActive</code> (default: true).
                </p>

                <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-x-auto text-gray-600">
{`[
  {
    "name": "Dobbs overturned Casey",
    "prompt": "What standard applies to abortion regulations?",
    "expectation": "Should recognize undue burden standard is no longer applicable law after Dobbs v. Jackson (2022)"
  }
]`}
                </pre>

                {importError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                    {importError}
                  </div>
                )}

                <textarea
                  value={importText}
                  onChange={e => setImportText(e.target.value)}
                  rows={12}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                  placeholder='[{"name": "...", "prompt": "...", "expectation": "..."}]'
                />
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowImportModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleImportTests}
                  disabled={importLoading || !importText.trim()}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {importLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                  Import
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Execution Detail Modal */}
      {selectedExecutionId && (
        <ExecutionHistoryDetail
          executionId={selectedExecutionId}
          onClose={() => { setSelectedExecutionId(null); setSelectedResult(null); }}
          hallucinationReasoning={selectedResult?.reasoning}
          hallucinationScore={selectedResult?.score}
          hallucinationTestName={selectedResult?.testName}
          hallucinationExpectation={selectedResult?.expectation}
        />
      )}
    </div>
    </AppLayout>
  );
};
